import React, { useState, useCallback, useEffect } from 'react';
import { Box, Button, CardMedia, Modal, ToggleButtonGroup, ToggleButton, Typography, Slider } from '@mui/material';
import Cropper from 'react-easy-crop';
import imageCompression from 'browser-image-compression';

import { getCroppedImg } from '../utils/images/getCroppedImg.js';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faImage, faCrop } from '@fortawesome/free-solid-svg-icons'
import DsasButton from './DsasButton/DsasButton.js';


const HEADER_RATIO = 16 / 5;

const RATIO_PRESETS = [
  { value: 16 / 5, css: '16 / 5', label: 'Bannière large' },
  { value: 16 / 9, css: '16 / 9', label: '16:9' },
  { value: 573 / 300, css: '573 / 300', label: 'Bandeau actuel' },
];

const HeaderHandlerTool = ({ initialImage = null, onImageSave,  onImageDelete, isEditable=true, type=null, displayImageLabel=true, uploadButtonLabel = 'Charger une image de bandeau' }) => {
    const [errorApiCall, setErrorApiCall] = useState(false);  
    const [previewImage, setPreviewImage] = useState(initialImage);
    const [uploadedImage, setUploadedImage] = useState(null);
    const [processedImage, setProcessedImage] = useState(null);
    const [uploadedFile, setUploadedFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [step, setStep] = useState(1); 
    const [showCropper, setShowCropper] = useState(false);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [aspect, setAspect] = useState(HEADER_RATIO);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

    const currentRatioPreset =
      RATIO_PRESETS.find((ratio) => ratio.value === aspect) || RATIO_PRESETS[0];
    

    useEffect(() => {
      setPreviewImage(initialImage);
    }, [initialImage]); // Met à jour `previewImage` dès que `initialImage` change

    const handleReset = () => {
      setPreviewImage(initialImage);
      setUploadedImage(null);
      setProcessedImage(null);
      setLoading(false);
      setStep(1);
      setShowCropper(false);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedAreaPixels(null);
      setUploadedFile(null);
      setAspect(HEADER_RATIO);
    };

    // PLAYER PHOTO EDITION
    // Etape 1 --> charger image par user. Affichage dans cadre preview1.
    // Etape 2 --> afficher boutons tools ('recadrer l'image' et 'supprimer l'arrière plan').
    // Etape 3 --> après supression background image, ou après redimentionnement image, afficher le résultat dans cadre de preview2
    // N'avoir que 2 cadres différents, 1 pour affichage preview image dans player data (preview1), et le 2e (preview2) ne s'affciche qu'après suppression de l'arrière plan ou redimensionnement pour que le user choisisse quelle version il garde.
    // Une fois version choisie on repart sur étape 1.

  // Handle image selection
  const handleSelectImage = async (e) => {
      const file = e.target.files[0];

      if (file) {
        try {
          const options = { maxSizeMB: 0.25, maxWidthOrHeight: 1024 };
          const compressedFile = await imageCompression(file, options);

          setUploadedFile(compressedFile);

          const reader = new FileReader();
          reader.onloadend = () => {
            setPreviewImage(reader.result);
            setUploadedImage(reader.result);
          };
          reader.readAsDataURL(compressedFile);

          setStep(2);
        } catch (error) {
          console.error('Image compression error:', error);
        }
      }
    };

  // Crop handling
  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleCropImage = async () => {
    if (!croppedAreaPixels) return;
    try {
      const croppedImage = await getCroppedImg(uploadedImage, croppedAreaPixels);
      setProcessedImage(croppedImage);
      //setPreviewImage(croppedImage);
      setShowCropper(false);
      setStep(3);
    } catch (error) {
      console.error("Error cropping image:", error);
    }
  };

  const handleSaveOriginalImage = () => {
    if (!uploadedFile) return;

    onImageSave(uploadedFile, {
      ratio: aspect,
    });

    setUploadedFile(null);
    setUploadedImage(null);
    setProcessedImage(null);
    setStep(1);
  };

  // Save the final chosen image and reset to step 1
  const handleSaveImageChoice = async (image) => {
      const convertToImageFile = async (imageData, fileName = 'image.jpg') => {
        if (typeof imageData === 'string' && imageData.startsWith('data:image/')) {
          const response = await fetch(imageData);
          const blob = await response.blob();
          return new File([blob], fileName, { type: blob.type });
        }

        if (imageData instanceof Blob) {
          return new File([imageData], fileName, { type: imageData.type });
        }

        return null;
      };

      const imageFile = await convertToImageFile(image);

      if (!imageFile) {
        console.error('L’image n’est ni un Blob ni un Base64.');
        return;
      }

      setPreviewImage(image);
      setUploadedImage(null);
      setProcessedImage(null);
      setUploadedFile(null);
      setStep(1);

      onImageSave(imageFile, {
        ratio: aspect,
      });
    };

  const headerPreviewWrapperSx = {
      width: '100%',
      maxWidth: 800,
      aspectRatio: currentRatioPreset.css,
      overflow: 'hidden',
    };

  const headerPreviewImageSx = {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    objectPosition: 'center center',
    display: 'block',
  };

    return (
    <Box>
        <Box sx={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
        {/* Preview 1: Image in player data */}
        <Box>
          {isEditable && displayImageLabel && <Box className='small-padding'><Typography variant="subtitle1" sx={{color: '#6D6E73'}}>Image actuelle</Typography></Box>}
          {previewImage && 
            <Box sx={headerPreviewWrapperSx}>
              <CardMedia
                component="img"
                image={previewImage}
                alt="Bandeau actuel"
                sx={headerPreviewImageSx}
              />
            </Box>
            }
            {!previewImage && 
            <Box className='default-image'>
              <FontAwesomeIcon icon={type ==='avatar' ? faUser : faImage} />
            </Box>
            }
        </Box>

        {/* Preview 2: Display processed image if available */}
        {processedImage && (
            <Box>
              {displayImageLabel && (
                <Typography variant="subtitle1" sx={{ color: '#6D6E73' }}>
                  Image modifiée
                </Typography>
              )}

              <Box sx={headerPreviewWrapperSx}>
                <CardMedia
                  component="img"
                  image={processedImage}
                  alt="Bandeau modifié"
                  sx={headerPreviewImageSx}
                />
              </Box>
            </Box>
          )}
      </Box>

      {/* Step 1: Upload image button */}
      {isEditable && step === 1 && (
        <Box className='centered-medium-wrapper'>
                 <DsasButton 
                  icon={faImage}
                  label={uploadButtonLabel}
                  variant="secondary" 
                  upload={true} 
                  onChange={handleSelectImage}
                />
         </Box>
      )}


      {/* Step 2: Editing tools */}
      <Box className="centered-medium-wrapper">
      {isEditable && (
        <>
        {step !== 1 && (
             <DsasButton
                label="Réinitialiser"
                variant="secondary"
                onClick={handleReset}
              />
          )}

        {step === 2 && (
        <>
          
            {uploadedFile && (
              <DsasButton
                label="Enregistrer cette image"
                variant="secondary"
                 onClick={handleSaveOriginalImage}
              />
            )}

            <DsasButton
              label="Recadrer"
              icon={faCrop}
              variant="secondary"
              onClick={() => setShowCropper(true)}
            />
         
        </>
        )}
       </>
      )}

      {previewImage && isEditable && onImageDelete && (
        <DsasButton
          label="Supprimer"
          variant="danger"
          onClick={onImageDelete}
        />
      )}

      </Box>

      {/* Cropper modal */}
      <Modal open={showCropper} onClose={() => setShowCropper(false)}>
        <Box sx={{ display: 'grid'}}>
          <Box style={{backgroundColor: 'red'}}>
            <Cropper
              image={uploadedImage}
              crop={crop}
              zoom={zoom}
              aspect={aspect}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
            </Box>
          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            flexDirection: 'row',
            alignItems: 'center',
            backgroundColor: 'white'
          }}>
            <Box sx={{display: 'inline-flex',
                    justifyContent: 'center',
                    alignContent: 'center',
                    alignItems: 'center',
                    flexWrap: 'nowrap',
                    flexDirection: 'row'
            }}>

              <ToggleButtonGroup
                  value={aspect}
                  exclusive
                  onChange={(e, newAspect) => {
                    if (!newAspect) return;       // éviter de remettre aspect à null
                    setAspect(newAspect);

                    // optionnel : recentrer / reset zoom à chaque changement de ratio
                    setCrop({ x: 0, y: 0 });
                    setZoom(1);
                    setCroppedAreaPixels(null);
                  }}
                  aria-label="Aspect ratio"
                  sx={{ m: 2, backgroundColor: 'rgb(255 255 255)' }}
                >
                  {RATIO_PRESETS.map((ratio) => (
                    <ToggleButton
                      key={ratio.label}
                      value={ratio.value}
                      aria-label={ratio.label}
                      sx={{ backgroundColor: 'rgb(255 255 255 / 82%)' }}
                    >
                      {ratio.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
          </Box> 
          {/* Slider de zoom */}
          <Box sx={{ px: 2, 
            minWidth: '150px', backgroundColor: 'rgb(255 255 255)', zIndex: 5, borderRadius: '6px', margin: '5px' }}>
            <Typography variant="body2" sx={{ mb: 1, color:'#47464B',
                  textAlign: 'center',
                  paddingTop: '5px',
                  margin: 0 }}>
              Zoom
            </Typography>
            <Slider
              value={zoom}
              min={1}
              max={4}
              step={0.1}
              onChange={(_, value) => setZoom(value)}
            />
          </Box>

          {/* Bouton appliquer */}
          <Box sx={{ backgroundColor: 'rgb(255 255 255)', margin: '10px' }}>
            <Button sx={{ backgroundColor: 'rgb(255 255 255 / 82%)', padding: '12px' }}  onClick={handleCropImage}>
              Appliquer
            </Button>
          </Box>
          </Box>
        </Box>
      </Modal>

      {/* Step 3: Choose final image */}
      {step === 3 && (
        <Box className='centered-medium-wrapper'>

          <DsasButton 
            label="Garder l'image originale" 
            variant="secondary" 
            onClick={() => handleSaveImageChoice(uploadedImage)}
          />

          <DsasButton 
            label="Garder l'image modifiée" 
            variant="secondary" 
            onClick={() => handleSaveImageChoice(processedImage)}
          />

        </Box>
        // <Box sx={{ mt: 2 }}>
        //   <Button variant="contained" onClick={() => handleSaveImageChoice(uploadedImage)}>
        //     Garder l'image originale
        //   </Button>
        //   <Button variant="contained" color="primary" onClick={() => handleSaveImageChoice(processedImage)} sx={{ ml: 2 }}>
        //     Garder l'image modifiée
        //   </Button>
        // </Box>
      )}
    </Box>
    )
};

export default HeaderHandlerTool;